import { useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Simple lightbox: images = [{ url, caption? }]
export default function Lightbox({ images, index, onClose, onIndex }) {
  const prev = useCallback(() => onIndex((index - 1 + images.length) % images.length), [index, images.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % images.length), [index, images.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  const cur = images[index];
  if (!cur) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-10"
      style={{ background: "rgba(15,15,13,0.94)" }}
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 md:top-8 md:right-8 text-white/70 hover:text-white transition"
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X size={28} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition p-2"
        data-testid="lightbox-prev"
        aria-label="Previous"
      >
        <ChevronLeft size={36} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition p-2"
        data-testid="lightbox-next"
        aria-label="Next"
      >
        <ChevronRight size={36} />
      </button>

      <figure
        className="max-w-[92vw] max-h-[86vh] flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={cur.url}
          alt={cur.caption || `Image ${index + 1}`}
          className="max-w-[92vw] max-h-[76vh] object-contain"
        />
        <figcaption className="text-center text-white/70 text-xs tracking-widest uppercase">
          {cur.caption ? <span className="mr-3">{cur.caption}</span> : null}
          <span>{index + 1} / {images.length}</span>
        </figcaption>
      </figure>
    </div>
  );
}
